<template>
  <drawer
    :visible.sync="visible"
    direction="rtl"
    size="600px"
    :modal="true"
    :withHeader="false"
    :append-to-body="true"
    class="default-drawer"
  >
    <Link :underline="false" class="nav-close" @click="handleClose">
      <svgicon name="cancelwhite" width="12" height="12" :original="true" />
    </Link>

    <div class="drawer-header px-15">
      <div style="display: flex;justify-content: space-between; width:100%">
        <span style="line-height: 30px">BULK POD UPLOAD</span>
        <div style="display: flex; justify-content: flex-end; gap: 10px;">
          <Button @click="handleClose">CANCEL</Button>
          <Button
            type="primary"
            @click="submitUpload"
            :loading="isSubmitting"
            :disabled="uploadedFiles.length === 0 || !mappingFile || transactionStatus !== 'PENDING_MAPPING'"
          >
            UPLOAD
          </Button>
        </div>
      </div>
    </div>

    <div class="drawer-body px-15">
      <div class="content-scroll-inner mx-pd-15" style="width:600px">
        <!-- Loading State -->
        <div v-if="isCheckingTransaction" style="padding: 40px; text-align: center;">
          <i class="el-icon-loading" style="font-size: 32px; color: #409EFF;"></i>
          <p style="margin-top: 15px; color: #606266;">Checking for active transaction...</p>
        </div>

        <!-- Main Content -->
        <div v-else>
        <!-- POD Upload Section -->
        <div class="card-plain">
          <div style="display: flex; gap: 15px; align-items: flex-start;">
            <div style="flex: 1;">
              <h4 class="card-list-title mb-5">POD UPLOAD</h4>
              <p class="text-muted mb-10" style="font-size: 12px;">
                Upload multiple files (.pdf, .jpeg, .jpg, .png, .xlsx) - Max 100 files, 10MB each
              </p>
            </div>
            <Upload
              ref="podUpload"
              class="default-upload flex-reverse is-badge"
              :auto-upload="false"
              :on-change="handleFileSelect"
              :file-list="[]"
              :show-file-list="false"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.xlsx"
              action="#"
              list-type="picture-card"
              :disabled="isInitializing || isUploading || uploadedFiles.length >= 100 || transactionStatus === 'UPLOAD_COMPLETED' || transactionStatus === 'UPLOAD_FAILED'"
            >
              <i class="el-icon-plus"></i>
            </Upload>
          </div>

          <!-- Initialization Progress -->
          <div v-if="isInitializing" class="mt-10" style="padding: 12px; background: #e8f4fd; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <i class="el-icon-loading" style="font-size: 16px; color: #409EFF;"></i>
              <span class="text-sm" style="font-weight: 600; color: #409EFF;">Initializing upload session...</span>
            </div>
          </div>

          <!-- Upload Progress -->
          <div v-if="isUploading" class="mt-10" style="padding: 12px; background: #f5f7fa; border-radius: 4px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <span class="text-sm" style="font-weight: 600;">Uploading files...</span>
              <span class="text-sm">{{ uploadProgress.current }} / {{ uploadProgress.total }}</span>
            </div>
            <Progress :percentage="uploadProgressPercentage" :status="uploadProgress.status"></Progress>
            <p v-if="uploadProgress.currentFile" class="text-muted" style="font-size: 11px; margin-top: 4px;">
              Current: {{ uploadProgress.currentFile }}
            </p>
          </div>
        </div>

        <!-- Delete Progress -->
        <div v-if="deleteProgress.isDeleting" class="mt-10" style="padding: 12px; background: #fef0f0; border-radius: 4px; border: 1px solid #F56C6C;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span class="text-sm" style="font-weight: 600; color: #F56C6C;">Deleting files...</span>
            <span class="text-sm" style="color: #F56C6C;">{{ deleteProgress.current }} / {{ deleteProgress.total }}</span>
          </div>
          <Progress :percentage="deleteProgressPercentage" status="exception"></Progress>
        </div>

        <!-- Successfully Uploaded Files -->
        <div v-if="uploadedFiles.length > 0" class="mt-10">
          <div class="file-list-header" @click="toggleUploadedList" style="min-height: 40px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <i :class="showUploadedList ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" style="font-size: 14px;"></i>
              <span class="text-sm" style="font-weight: 600; color: #67C23A;">
                <i class="el-icon-circle-check"></i> {{ uploadedFiles.length }} file(s) uploaded successfully
              </span>
            </div>
            <Button type="text" size="small" @click.stop="clearAllFiles" style="color: #F56C6C; padding: 5px 10px;" :disabled="isUploading || deleteProgress.isDeleting || transactionStatus === 'UPLOAD_COMPLETED' || transactionStatus === 'UPLOAD_FAILED'">
              CLEAR ALL
            </Button>
          </div>

          <div v-show="showUploadedList" class="file-list-container">
            <div v-for="file in uploadedFiles" :key="file.stored_name" class="file-list-item">
              <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                <i :class="getFileIcon(file)" style="font-size: 20px; color: #67C23A;"></i>
                <div style="flex: 1; min-width: 0;">
                  <div class="file-name">{{ file.stored_name }}</div>
                  <div class="file-size">{{ formatFileSize(file.size) }}</div>
                </div>
              </div>
              <Button
                type="text"
                size="mini"
                @click="removeFile(file.stored_name)"
                style="color: #F56C6C; padding: 4px 8px;"
                :disabled="isUploading || deleteProgress.isDeleting"
              >
                <i class="el-icon-delete" style="font-size: 14px;"></i>
              </Button>
            </div>
          </div>
        </div>

        <!-- Rejected Files -->
        <div v-if="rejectedFiles.length > 0" class="mt-10">
          <div class="file-list-header" @click="toggleRejectedList" style="min-height: 40px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <i :class="showRejectedList ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" style="font-size: 14px;"></i>
              <span class="text-sm" style="font-weight: 600; color: #F56C6C;">
                <i class="el-icon-circle-close"></i> {{ rejectedFiles.length }} file(s) rejected
              </span>
            </div>
            <a href="javascript:void(0)" @click.stop="downloadUploadRejections" style="color: #F56C6C; font-size: 16px; text-decoration: none;">
              <i class="el-icon-download"></i>
            </a>
          </div>

          <div v-show="showRejectedList" class="file-list-container">
            <div v-for="(file, index) in rejectedFiles" :key="index" class="file-list-item rejected-item">
              <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                <i class="el-icon-warning" style="font-size: 16px; color: #F56C6C;"></i>
                <div style="flex: 1; min-width: 0;">
                  <div class="file-name">{{ file.file_name }}</div>
                  <div class="file-size" style="color: #F56C6C;">{{ file.reason }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div style="margin: 20px 0; border-top: 1px solid #e4e7ed;"></div>

        <!-- Processing Status Alert -->
        <div v-if="transactionStatus === 'UPLOAD_IN_PROGRESS'" class="mt-10 mb-10" style="padding: 12px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <i class="el-icon-loading" style="font-size: 16px; color: #e6a23c;"></i>
            <span style="font-size: 13px; font-weight: 600; color: #856404;">Processing POD attachments in progress...</span>
          </div>
          <p style="font-size: 12px; color: #856404; margin-top: 6px; margin-bottom: 0;">Please wait for the current upload to complete. You cannot upload a new mapping while processing is in progress.</p>
        </div>

        <!-- Processing Completed Summary -->
        <div v-if="transactionStatus === 'UPLOAD_COMPLETED'" class="mt-10 mb-10">
          <!-- Success Summary -->
          <div style="padding: 16px; background: #f0f9ff; border: 1px solid #409EFF; border-radius: 4px; margin-bottom: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <div style="font-size: 14px; font-weight: 600; color: #303133; margin-bottom: 8px;">
                  <i class="el-icon-circle-check" style="color: #67C23A;"></i> Upload Process Completed
                </div>
                <div style="display: flex; gap: 24px; font-size: 13px;">
                  <div>
                    <span style="color: #909399;">Total:</span>
                    <span style="font-weight: 600; color: #303133; margin-left: 4px;">{{ successfulUploads.length + processingRejections.length }}</span>
                  </div>
                  <div>
                    <span style="color: #909399;">Success:</span>
                    <span style="font-weight: 600; color: #67C23A; margin-left: 4px;">{{ successfulUploads.length }}</span>
                  </div>
                  <div>
                    <span style="color: #909399;">Failed:</span>
                    <span style="font-weight: 600; color: #F56C6C; margin-left: 4px;">{{ processingRejections.length }}</span>
                  </div>
                </div>
              </div>
              <Button type="danger" size="small" @click="handleDiscardTransaction">
                DISCARD TRANSACTION
              </Button>
            </div>
          </div>

          <!-- Successful Uploads -->
          <div v-if="successfulUploads.length > 0" class="mb-10">
            <div class="file-list-header" @click="toggleSuccessList" style="min-height: 40px; background: #f0f9ff; border: 1px solid #67C23A;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <i :class="showSuccessList ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" style="font-size: 14px;"></i>
                <span class="text-sm" style="font-weight: 600; color: #67C23A;">
                  <i class="el-icon-circle-check"></i> {{ successfulUploads.length }} LR attachment(s) uploaded successfully
                </span>
              </div>
              <a href="javascript:void(0)" @click.stop="downloadSuccessReport" style="color: #67C23A; font-size: 16px; text-decoration: none;">
                <i class="el-icon-download"></i>
              </a>
            </div>

            <div v-show="showSuccessList" class="file-list-container" style="border: 1px solid #67C23A;">
              <div v-for="(upload, index) in successfulUploads" :key="index" class="file-list-item" style="background: #f0f9ff;">
                <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                  <i class="el-icon-circle-check" style="font-size: 16px; color: #67C23A;"></i>
                  <div style="flex: 1; min-width: 0;">
                    <div class="file-name">LR: {{ upload.lr_number }} → File: {{ upload.file_name }}</div>
                    <div class="file-size" style="color: #67C23A;">
                      Successfully attached to Indent: <strong>{{ upload.indent_no }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Failed Uploads -->
          <div v-if="processingRejections.length > 0">
            <div class="file-list-header" @click="toggleProcessingRejectedList" style="min-height: 40px; background: #fef0f0; border: 1px solid #f56c6c;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <i :class="showProcessingRejectedList ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" style="font-size: 14px;"></i>
                <span class="text-sm" style="font-weight: 600; color: #F56C6C;">
                  <i class="el-icon-circle-close"></i> {{ processingRejections.length }} LR(s) failed
                </span>
              </div>
              <a href="javascript:void(0)" @click.stop="downloadRejectionReport" style="color: #F56C6C; font-size: 16px; text-decoration: none;">
                <i class="el-icon-download"></i>
              </a>
            </div>

            <div v-show="showProcessingRejectedList" class="file-list-container" style="border: 1px solid #f56c6c;">
              <div v-for="(rejection, index) in processingRejections" :key="index" class="file-list-item rejected-item">
                <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                  <i class="el-icon-warning" style="font-size: 16px; color: #F56C6C;"></i>
                  <div style="flex: 1; min-width: 0;">
                    <div class="file-name">LR: {{ rejection.lr_number }} → File: {{ rejection.file_name }}</div>
                    <div class="file-size" style="color: #F56C6C;">{{ rejection.reason }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mapping Section -->
        <div class="card-plain">
          <div style="background: #f0f9ff; padding: 8px 12px; border-radius: 4px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <i class="el-icon-document" style="color: #67C23A; font-size: 16px;"></i>
              <span style="font-size: 13px; font-weight: 500;">Indent to POD mapping</span>
            </div>
            <Button
              type="text"
              size="small"
              @click.stop="downloadTemplate"
              style="color: #409EFF; font-weight: 600; font-size: 12px; padding: 0;"
              :disabled="uploadedFiles.length === 0 || isUploading || transactionStatus !== 'PENDING_MAPPING'"
            >
              SAMPLE DOWNLOAD
            </Button>
          </div>

          <div style="display: flex; gap: 15px; align-items: flex-start;">
            <div style="flex: 1;">
              <h4 class="card-list-title mb-5">UPLOAD DOCUMENT</h4>
              <p class="text-muted mb-10" style="font-size: 12px;">(.xlsx)</p>
              <div v-if="mappingFile" class="mt-8" style="display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #f5f7fa; border-radius: 4px;">
                <span class="text-sm">{{ mappingFile.name }}</span>
                <Button type="text" size="small" @click="clearMappingFile">Remove</Button>
              </div>
            </div>
            <Upload
              ref="mappingUpload"
              class="default-upload flex-reverse is-badge"
              :auto-upload="false"
              :on-change="handleMappingFileChange"
              :file-list="[]"
              :show-file-list="false"
              :limit="1"
              accept=".xlsx"
              action="#"
              list-type="picture-card"
              :disabled="uploadedFiles.length === 0 || isUploading || transactionStatus !== 'PENDING_MAPPING'"
            >
              <i class="el-icon-plus"></i>
            </Upload>
          </div>
        </div>

        <!-- Submit Progress Overlay -->
        <div v-if="showSubmitProgress" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.95); display: flex; align-items: center; justify-content: center; z-index: 100;">
          <div style="width: 400px; padding: 30px; background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.15);">
            <h4 style="text-align: center; margin-bottom: 20px;">Processing Upload</h4>
            <Progress :percentage="submitProgress" :status="submitProgressStatus"></Progress>
            <p style="text-align: center; margin-top: 10px; color: #606266;">{{ submitProgressText }}</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  </drawer>
</template>

<script>
import { mapActions } from 'vuex';
// eslint-disable-next-line import/no-extraneous-dependencies
import { POD_BULK_PERMISSION_DENIED_TYPE } from 'pando-constants';

export default {
  name: 'BulkPodUpload',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bulkUploadId: null,
      transactionStatus: null, // PENDING_MAPPING, UPLOAD_IN_PROGRESS, UPLOAD_COMPLETED, UPLOAD_FAILED
      isCheckingTransaction: false,
      isInitializing: false,
      uploadedFiles: [],
      rejectedFiles: [],
      processingRejections: [], // LR validation errors from worker
      successfulUploads: [], // Successfully uploaded LR attachments
      pendingFiles: [],
      mappingFile: null,
      isUploading: false,
      isSubmitting: false,
      statusPollInterval: null,
      uploadProgress: {
        current: 0,
        total: 0,
        currentFile: '',
        status: '',
      },
      deleteProgress: {
        current: 0,
        total: 0,
        isDeleting: false,
      },
      submitProgress: 0,
      submitProgressText: '',
      submitProgressStatus: '',
      showSubmitProgress: false,
      showUploadedList: false,
      showRejectedList: false,
      showSuccessList: false,
      showProcessingRejectedList: false,
    };
  },
  computed: {
    uploadProgressPercentage() {
      if (this.uploadProgress.total === 0) return 0;
      const percentage = Math.round((this.uploadProgress.current / this.uploadProgress.total) * 100);
      return Math.min(percentage, 100); // Cap at 100%
    },
    deleteProgressPercentage() {
      if (this.deleteProgress.total === 0) return 0;
      const percentage = Math.round((this.deleteProgress.current / this.deleteProgress.total) * 100);
      return Math.min(percentage, 100); // Cap at 100%
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.checkForActiveTransaction();
      }
    },
  },
  methods: {
    ...mapActions('order', [
      'initializeBulkUpload',
      'uploadSingleFile',
      'listFiles',
      'deleteFile',
      'downloadTemplate',
      'uploadMappingExcel',
      'checkActiveTransaction',
      'fetchBulkUploadStatus',
      'discardTransaction',
    ]),

    notifyPermissionRejections(rejections) {
      const count = rejections.filter(r => r.rejection_type === POD_BULK_PERMISSION_DENIED_TYPE).length;
      if (count === 0) return;
      this.$errorMessage({
        message: `POD upload restricted: you do not have permission for ${count} row(s). Check the rejection details below.`,
      });
    },

    async checkForActiveTransaction() {
      this.isCheckingTransaction = true;
      try {
        const result = await this.checkActiveTransaction();

        if (result.has_active) {
          this.bulkUploadId = result.bulk_upload_id;
          this.transactionStatus = result.status;
          await this.loadExistingFiles();
        } else {
          console.log('[BulkPodUpload] No active transaction. Will create on first file upload.');
        }
      } catch (error) {
        console.error('[BulkPodUpload] Check active transaction error:', error);
      } finally {
        this.isCheckingTransaction = false;
      }
    },

    async initializeUpload() {
      if (this.isInitializing || this.bulkUploadId) return;

      this.isInitializing = true;
      try {
        // Initialize transaction
        const result = await this.initializeBulkUpload();

        // Check for error from HandleResponses.Status
        if (result.error) {
          this.$errorMessage({ message: result.error });
          return;
        }

        // Check for API errors array
        if (result.errors) {
          this.$errorMessage({ message: result.errors[0].message || 'Failed to initialize upload' });
          return;
        }

        // Success - should have bulk_upload_id
        if (!result.bulk_upload_id) {
          console.error('[BulkPodUpload] No bulk_upload_id in result:', result);
          this.$errorMessage({ message: 'Invalid response from server' });
          return;
        }

        this.bulkUploadId = result.bulk_upload_id;
        this.transactionStatus = result.status || 'PENDING_MAPPING';

        // If resuming existing transaction, load files
        if (result.existing) {
          await this.loadExistingFiles();
        }
      } catch (error) {
        console.error('[BulkPodUpload] Initialize error:', error);
        this.$errorMessage({ message: error.message || 'Failed to initialize upload' });
      } finally {
        this.isInitializing = false;
      }
    },

    async loadExistingFiles() {
      try {
        // If transaction is completed, fetch status to get results
        if (this.transactionStatus === 'UPLOAD_COMPLETED' || this.transactionStatus === 'UPLOAD_FAILED') {
          const statusResult = await this.fetchBulkUploadStatus(this.bulkUploadId);
          if (!statusResult.errors) {
            this.successfulUploads = statusResult.valid_entries || [];
            this.processingRejections = statusResult.invalid_entries || [];
            this.notifyPermissionRejections(this.processingRejections);
          }
        } else {
          // For pending/in-progress, list files
          const result = await this.listFiles(this.bulkUploadId);
          if (result.errors) {
            return;
          }

          this.uploadedFiles = result.uploaded_files || [];
          this.rejectedFiles = result.rejected_files || [];
        }
      } catch (error) {
        console.error('Failed to load existing files:', error);
      }
    },

    async handleFileSelect(file, fileList) {
      // Initialize transaction on first file selection
      if (!this.bulkUploadId && !this.isInitializing) {
        await this.initializeUpload();
      }

      // Wait for initialization if still in progress
      if (this.isInitializing) {
        await new Promise(resolve => {
          const checkInit = setInterval(() => {
            if (!this.isInitializing) {
              clearInterval(checkInit);
              resolve();
            }
          }, 100);
        });
      }

      if (!this.bulkUploadId) {
        this.$errorMessage({ message: 'Failed to initialize upload. Please try again.' });
        return;
      }

      // Validate file count
      if (this.uploadedFiles.length >= 100) {
        this.$errorMessage({ message: 'Maximum 100 files allowed' });
        return;
      }

      // Add to pending queue
      this.pendingFiles.push(file.raw);

      // Start upload if not already uploading
      if (!this.isUploading) {
        await this.processUploadQueue();
      }
    },

    async processUploadQueue() {
      if (this.pendingFiles.length === 0 || this.isUploading) {
        return;
      }

      this.isUploading = true;
      // Capture the initial total count at the start of processing
      const initialTotal = this.pendingFiles.length;
      this.uploadProgress.total = initialTotal;
      this.uploadProgress.current = 0;
      this.uploadProgress.status = '';

      // Process only the files that were pending at the start
      for (let i = 0; i < initialTotal; i++) {
        const file = this.pendingFiles.shift();
        this.uploadProgress.currentFile = file.name;

        try {
          const result = await this.uploadSingleFile({
            bulkUploadId: this.bulkUploadId,
            file,
          });

          // Check for HandleResponses.Status error format
          if (result.error) {
            this.rejectedFiles.push({
              file_name: file.name,
              reason: result.error,
            });
          }
          // Check for API errors array
          else if (result.errors) {
            this.rejectedFiles.push({
              file_name: file.name,
              reason: result.errors[0].message || result.errors[0].field || 'Upload failed',
            });
          }
          // Check for success response
          else if (result.success) {
            this.uploadedFiles.push(result.file);
          }
          // Backend returned success: false with reason
          else if (result.success === false && result.reason) {
            this.rejectedFiles.push({
              file_name: file.name,
              reason: result.reason,
            });
          }
          // Unknown response format
          else {
            this.rejectedFiles.push({
              file_name: file.name,
              reason: 'Unknown error occurred',
            });
          }
        } catch (error) {
          console.error('[BulkPodUpload] Upload error for', file.name, ':', error);
          this.rejectedFiles.push({
            file_name: file.name,
            reason: error.message || error.reason || 'Upload failed',
          });
        }

        this.uploadProgress.current++;
      }

      this.isUploading = false;
      this.uploadProgress.currentFile = '';
      this.uploadProgress.status = 'success';

      // Clear upload component
      if (this.$refs.podUpload) {
        this.$refs.podUpload.clearFiles();
      }

      // If more files were added while processing, start a new batch
      if (this.pendingFiles.length > 0) {
        await this.processUploadQueue();
      }
    },

    async removeFile(fileName) {
      try {
        const result = await this.deleteFile({
          bulkUploadId: this.bulkUploadId,
          fileName,
        });

        if (result.errors) {
          this.$errorMessage({ message: result.errors[0].message || 'Failed to delete file' });
          return;
        }

        // Remove from uploaded files
        const index = this.uploadedFiles.findIndex(f => f.stored_name === fileName);
        if (index > -1) {
          this.uploadedFiles.splice(index, 1);
        }

        this.$successMessage({ message: 'File removed successfully' });
      } catch (error) {
        this.$errorMessage({ message: error.message || 'Failed to delete file' });
      }
    },

    async clearAllFiles() {
      // In PENDING_MAPPING stage, CLEAR ALL = Discard entire transaction
      if (this.transactionStatus === 'PENDING_MAPPING') {
        this.$PromptConfirm('Are you sure you want to discard this upload? All files will be removed.', 'Confirm', {
          confirmButtonText: 'Yes, Discard',
          cancelButtonText: 'No',
        }).then(async () => {
          try {
            const result = await this.discardTransaction(this.bulkUploadId);
            if (result.success) {
              this.$successMessage({ message: 'Transaction discarded successfully' });
              this.$emit('complete');
              this.handleClose();
            } else {
              this.$errorMessage({ message: result.error || 'Failed to discard transaction' });
            }
          } catch (error) {
            console.error('[BulkPodUpload] Discard error:', error);
            this.$errorMessage({ message: 'Failed to discard transaction' });
          }
        }).catch(() => {});
      } else {
        // For other statuses (fallback to individual delete if needed)
        this.$PromptConfirm('Are you sure you want to remove all uploaded files?', 'Confirm', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        }).then(async () => {
          this.deleteProgress.isDeleting = true;
          this.deleteProgress.total = this.uploadedFiles.length;
          this.deleteProgress.current = 0;

          const filesToDelete = [...this.uploadedFiles]; // Create a copy to iterate

          for (const file of filesToDelete) {
            try {
              await this.deleteFile({
                bulkUploadId: this.bulkUploadId,
                fileName: file.stored_name,
              });
              this.deleteProgress.current++;
            } catch (error) {
              console.error('[BulkPodUpload] Error deleting file:', file.stored_name, error);
              this.deleteProgress.current++;
            }
          }

          this.uploadedFiles = [];
          this.deleteProgress.isDeleting = false;
          this.$successMessage({ message: 'All files removed' });
        }).catch(() => {});
      }
    },

    toggleUploadedList() {
      this.showUploadedList = !this.showUploadedList;
    },

    toggleRejectedList() {
      this.showRejectedList = !this.showRejectedList;
    },

    toggleSuccessList() {
      this.showSuccessList = !this.showSuccessList;
    },

    toggleProcessingRejectedList() {
      this.showProcessingRejectedList = !this.showProcessingRejectedList;
    },

    async downloadSuccessReport() {
      try {
        // Generate Excel from successfulUploads data
        const XLSX = await import('xlsx');
        const data = this.successfulUploads.map(upload => ({
          'LR Number': upload.lr_number,
          'File Name': upload.file_name,
          'Indent Number': upload.indent_no,
          'Status': 'Success',
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Successful Uploads');

        XLSX.writeFile(wb, `pod_bulk_upload_success_${this.bulkUploadId}.xlsx`);
        this.$successMessage({ message: 'Success report downloaded' });
      } catch (error) {
        console.error('Download success report error:', error);
        this.$errorMessage({ message: 'Failed to download success report' });
      }
    },

    async downloadRejectionReport() {
      try {
        // Generate Excel from processingRejections data
        const XLSX = await import('xlsx');
        const data = this.processingRejections.map(rejection => ({
          'LR Number': rejection.lr_number,
          'File Name': rejection.file_name,
          'Reason': rejection.reason,
          'Suggested Action': rejection.suggested_action || '',
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Rejections');

        XLSX.writeFile(wb, `pod_bulk_upload_rejections_${this.bulkUploadId}.xlsx`);
        this.$successMessage({ message: 'Rejection report downloaded' });
      } catch (error) {
        console.error('Download rejection report error:', error);
        this.$errorMessage({ message: 'Failed to download rejection report' });
      }
    },

    async downloadUploadRejections() {
      try {
        // Generate Excel from rejectedFiles data (initial upload rejections)
        const XLSX = await import('xlsx');
        const data = this.rejectedFiles.map(rejection => ({
          'File Name': rejection.file_name,
          'Reason': rejection.reason,
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Upload Rejections');

        XLSX.writeFile(wb, `pod_upload_rejections_${this.bulkUploadId}.xlsx`);
        this.$successMessage({ message: 'Upload rejection report downloaded' });
      } catch (error) {
        console.error('Download upload rejections error:', error);
        this.$errorMessage({ message: 'Failed to download upload rejections' });
      }
    },

    getFileIcon(file) {
      const ext = file.stored_name.split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'el-icon-document';
      if (['jpg', 'jpeg', 'png'].includes(ext)) return 'el-icon-picture';
      if (ext === 'xlsx') return 'el-icon-s-grid';
      return 'el-icon-document';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },

    handleMappingFileChange(file, fileList) {
      if (!file.name.endsWith('.xlsx')) {
        this.$errorMessage({ message: 'Invalid file type. Only .xlsx files are allowed.' });
        this.$refs.mappingUpload.clearFiles();
        return;
      }

      this.mappingFile = file;
    },

    clearMappingFile() {
      this.mappingFile = null;
      this.$refs.mappingUpload.clearFiles();
    },

    async downloadTemplate() {
      try {
        if (this.uploadedFiles.length === 0) {
          this.$warningMessage({ message: 'Please upload POD files first' });
          return;
        }

        await this.$store.dispatch('order/downloadTemplate', this.bulkUploadId);
        this.$successMessage({ message: 'Template downloaded successfully' });
      } catch (error) {
        this.$errorMessage({ message: error.message || 'Failed to download template' });
      }
    },

    async submitUpload() {
      if (this.uploadedFiles.length === 0) {
        this.$errorMessage({ message: 'Please upload POD files first' });
        return;
      }

      if (!this.mappingFile) {
        this.$errorMessage({ message: 'Please upload the mapping Excel file' });
        return;
      }

      try {
        this.isSubmitting = true;
        this.showSubmitProgress = true;
        this.submitProgress = 0;
        this.submitProgressText = 'Uploading mapping file...';
        this.submitProgressStatus = '';

        this.submitProgress = 50;
        const result = await this.uploadMappingExcel({
          bulkUploadId: this.bulkUploadId,
          file: this.mappingFile.raw,
        });

        if (result.errors) {
          throw new Error(result.errors[0].message || 'Failed to upload mapping file');
        }

        if (result.error) {
          throw new Error(result.error);
        }

        this.submitProgress = 100;
        this.submitProgressText = 'Upload completed!';
        this.submitProgressStatus = 'success';

        setTimeout(() => {
          this.$successMessage({ message: 'Bulk POD upload initiated successfully! Processing in background...' });
          this.showSubmitProgress = false;
          // Start polling for status
          this.startStatusPolling();
        }, 1500);

      } catch (error) {
        this.submitProgress = 100;
        this.submitProgressStatus = 'exception';
        this.submitProgressText = error.message || 'Upload failed';
        this.$errorMessage({ message: error.message || 'Failed to upload files' });

        setTimeout(() => {
          this.showSubmitProgress = false;
        }, 2000);
      } finally {
        this.isSubmitting = false;
      }
    },

    startStatusPolling() {
      if (this.statusPollInterval) {
        clearInterval(this.statusPollInterval);
      }

      this.statusPollInterval = setInterval(async () => {
        try {
          const result = await this.fetchBulkUploadStatus(this.bulkUploadId);

          if (result.errors) {
            this.stopStatusPolling();
            return;
          }

          this.transactionStatus = result.status;

          // Update processing results
          if (result.invalid_entries && result.invalid_entries.length > 0) {
            this.processingRejections = result.invalid_entries;
            this.notifyPermissionRejections(result.invalid_entries);
          }
          if (result.valid_entries && result.valid_entries.length > 0) {
            this.successfulUploads = result.valid_entries;
          }

          // Stop polling when completed or failed
          if (result.status === 'UPLOAD_COMPLETED' || result.status === 'UPLOAD_FAILED') {
            this.stopStatusPolling();

            if (result.status === 'UPLOAD_COMPLETED') {
              this.$successMessage({
                message: `Processing completed! ${result.success} PODs attached successfully.${result.failed > 0 ? ` ${result.failed} failed.` : ''}`
              });
              this.$emit('upload-complete', this.bulkUploadId);
            } else {
              this.$errorMessage({ message: 'Processing failed. Please check the logs.' });
            }
          }
        } catch (error) {
          console.error('[BulkPodUpload] Status polling error:', error);
          this.stopStatusPolling();
        }
      }, 3000); // Poll every 3 seconds
    },

    stopStatusPolling() {
      if (this.statusPollInterval) {
        clearInterval(this.statusPollInterval);
        this.statusPollInterval = null;
      }
    },

    async handleDiscardTransaction() {
      this.$PromptConfirm('Are you sure you want to discard this transaction? All uploaded files and mappings will be lost.', 'Confirm', {
        confirmButtonText: 'Yes, Discard',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }).then(async () => {
        try {
          const result = await this.discardTransaction(this.bulkUploadId);
          if (result.errors) {
            this.$errorMessage({ message: result.errors[0].message || 'Failed to discard transaction' });
            return;
          }

          this.$successMessage({ message: 'Transaction discarded successfully' });
          this.resetForm();
        } catch (error) {
          this.$errorMessage({ message: error.message || 'Failed to discard transaction' });
        }
      }).catch(() => {});
    },

    handleClose() {
      if (this.isUploading || this.isSubmitting) {
        this.$PromptConfirm('Upload in progress. Are you sure you want to cancel?', 'Warning', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        }).then(() => {
          this.stopStatusPolling();
          this.resetForm();
          this.$emit('update:visible', false);
        }).catch(() => {});
      } else {
        this.stopStatusPolling();
        this.resetForm();
        this.$emit('update:visible', false);
      }
    },

    resetForm() {
      this.stopStatusPolling();
      this.bulkUploadId = null;
      this.transactionStatus = null;
      this.uploadedFiles = [];
      this.rejectedFiles = [];
      this.processingRejections = [];
      this.successfulUploads = [];
      this.pendingFiles = [];
      this.mappingFile = null;
      this.isUploading = false;
      this.isSubmitting = false;
      this.uploadProgress = {
        current: 0,
        total: 0,
        currentFile: '',
        status: '',
      };
      this.submitProgress = 0;
      this.submitProgressText = '';
      this.submitProgressStatus = '';
      this.showSubmitProgress = false;
      if (this.$refs.mappingUpload) this.$refs.mappingUpload.clearFiles();
      if (this.$refs.podUpload) this.$refs.podUpload.clearFiles();
    },
  },
  beforeDestroy() {
    this.stopStatusPolling();
  },
};
</script>

<style scoped>
.card-list-title {
  font-size: 13px;
  font-weight: 700;
  color: #303133;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.text-muted {
  font-size: 12px;
  color: #909399;
}

.text-sm {
  font-size: 12px;
  color: #606266;
}

.file-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
}

.file-list-header:hover {
  background: #ebeef5;
}

.file-list-container {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-top: 5px;
}

.file-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}

.file-list-item:last-child {
  border-bottom: none;
}

.file-list-item:hover {
  background: #f5f7fa;
}

.rejected-item {
  background: #fef0f0;
}

.rejected-item:hover {
  background: #fde2e2;
}

.file-name {
  font-size: 12px;
  color: #303133;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}
</style>
